import { Backend } from '@omq/shared';
import type { User } from '@omq/types';

/**
 * Provide additional API for login.
 *
 * @type {{generateAPIKey: ((function(Backend): Promise<string>)|*)}}
 */
export const LoginAPI = {
  /**
   * Perform login for passed credentials.
   *
   * @param {Backend} backend - backend object for requests.
   * @param {string} username - username
   * @param {string} password - password
   * @param {boolean} useAdminContext - use admin context for login
   *
   * @returns {Promise<User|null>}
   */
  login: async (
    backend: Backend,
    username: string,
    password: string,
    useAdminContext: boolean = false,
  ): Promise<User> => {
    const data = {
      email: username,
      password,
    };

    // send request & return user
    return await backend.post<User>(`${useAdminContext ? 'admin/' : ''}login`, data);
  },

  /**
   * Submit TOTP for login.
   *
   * @param backend - backend object for requests.
   * @param totp - totp code
   * @returns {Promise<void>}
   */
  totp: async (backend: Backend, totp: string): Promise<void> => {
    return await backend.post<void>('login/totp', {
      totp,
    });
  },

  /**
   * Request QR code for login.
   *
   * @param backend - backend object for requests.
   * @returns {Promise<void>}
   */
  qrcode: async (backend: Backend): Promise<void> => {
    return await backend.get<void>('login/qrcode');
  },

  /**
   * Check if TOTP setup is needed.
   * Done by checking if query to /login/qrcode is successful.
   *
   * @param backend - backend object for requests.
   * @returns {Promise<void>}
   */
  isTotpSetupNeeded: async (backend: Backend): Promise<void> => {
    return await backend.get<void>('login/qrcode');
  },

  /**
   * Login for integrations via API key.
   *
   * @param {Backend} backend - backend object for requests.
   *
   * @returns {Promise<User|null>}
   */
  loginWithApiKey: async (backend: Backend): Promise<User> => {
    return await backend.post<User>('login');
  },

  /**
   * Fetch current user.
   *
   * @param {Backend} backend - backend object for requests.
   * @param {boolean} useAdminContext - use admin context for request
   *
   * @returns {Promise<User|null>}
   */
  getCurrentUser: async (backend: Backend, useAdminContext: boolean = false): Promise<User> => {
    return await backend.get<User>(`${useAdminContext ? 'admin/' : ''}login/me`);
  },

  /**
   * Send request to reset password for given account.
   *
   * @param {Backend} backend
   * @param {string} email - email/username
   * @param {string} language - language
   *
   * @returns {Promise<void|{error: string}|{error}>}
   */
  resetPassword: async (backend: Backend, email: string, language: string): Promise<void> => {
    const data = {
      email,
      language,
    };

    return await backend.post('users/forgot_password', data);
  },

  /**
   * Send request to logout current user.
   *
   * @param {Backend} backend
   *
   * @returns {Promise<void|{error: string}|{error}>}
   */
  logout: async (backend: Backend): Promise<void> => {
    return await backend.post('logout');
  },

  /**
   * Send request to update password of a user.
   *
   * @param backend
   * @param userId - id of user
   * @param password - new password
   * @param params - security params
   *
   * @returns {Promise<void>}
   */
  updatePassword: async (
    backend: Backend,
    userId: string,
    password: string,
    params: {
      user: string;
      signature: string;
      expiration: string;
    },
  ): Promise<void> => {
    return await backend.put(
      `users/${userId}`,
      {
        password,
      },
      {
        params,
      },
    );
  },
};
